/**
 * 매뉴얼 콘텐츠 파서 (순수 함수).
 *
 * `docs/manual/**\/*.md`의 프론트매터·경로를 해석해 **매뉴얼 → 섹션 → 문서** 3층 트리로 만든다.
 * 파일 수집(`import.meta.glob`)은 `manualContent.ts`가 담당 — 이 모듈은 입력을 받기만 해서
 * 단위 테스트가 실제 파일 시스템에 의존하지 않는다.
 *
 * 최상위 단위는 **매뉴얼**이다(Composer는 그중 첫 번째일 뿐 — 결재라인 메타·법규 관리 등이 뒤따른다).
 * 6개 섹션 어휘는 매뉴얼들이 **공유**하며, 매뉴얼마다 전부 채울 필요는 없다.
 *
 * 집필 규약 정본: `docs/manual/README.md`
 */

/** 섹션 표시 순서·한글 라벨 (규약 고정 — 모든 매뉴얼이 공유하는 어휘). */
export const MANUAL_SECTIONS = [
  { id: 'getting-started', label: '처음이신가요?' },
  { id: 'tutorials', label: '따라 만들기' },
  { id: 'how-to', label: '이럴 땐 이렇게' },
  { id: 'concepts', label: '알아두기' },
  { id: 'reference', label: '찾아보기' },
  { id: 'troubleshooting', label: '잘 안될 때' },
] as const;

export type ManualSectionId = (typeof MANUAL_SECTIONS)[number]['id'];

const SECTION_ORDER = new Map<string, number>(
  MANUAL_SECTIONS.map((s, i) => [s.id, i]),
);

export const MANUAL_SECTION_LABELS: Record<string, string> = Object.fromEntries(
  MANUAL_SECTIONS.map((s) => [s.id, s.label]),
);

/** 프론트매터에서 항상 배열로 다루는 키. */
const ARRAY_KEYS = ['audience', 'covers', 'related'] as const;

/** 없으면 문서가 결함으로 표시되는 키. */
const REQUIRED_KEYS = ['title', 'summary', 'order'] as const;

export interface ManualDocMeta {
  /** 레포 기준 원본 경로 (`docs/manual/composer/concepts/c1-two-layers.md`) */
  path: string;
  /** 매뉴얼 id = 최상위 디렉토리명 (`composer`) */
  manual: string;
  section: string;
  slug: string;
  /** 표시용 제목. 프론트매터에 없으면 slug가 들어간다. */
  title: string;
  summary: string;
  order: number;
  audience: string[];
  level?: string;
  readingTime?: number;
  covers: string[];
  related: string[];
  /**
   * 사람이 마지막으로 내용을 확인한 날(`YYYY-MM-DD`).
   *
   * L2 드리프트 판정에서 **문서 커밋보다 우선하는 기준 시각**이다(`scripts/manual/drift.mjs`).
   * 오탈자 수정 커밋이 "읽어봤고 정확하다"를 덮어쓰지 않게 하려는 것 — 화면 표시는 하지 않는다.
   */
  reviewedAt?: string;
  generated: boolean;
  /** 필수 프론트매터 키가 빠진 문서 — 목록에 결함 표시가 붙는다. */
  incomplete: boolean;
  missingKeys: string[];
}

export interface ManualDoc extends ManualDocMeta {
  /** 프론트매터를 제거한 본문 마크다운. */
  body: string;
}

/**
 * 매뉴얼 표지 (`<매뉴얼>/index.md`).
 *
 * 섹션에 속하지 않으므로 `section`/`slug`가 없고, `order`는 **매뉴얼 목록에서의 순서**를 뜻한다.
 * 본문은 그 매뉴얼 랜딩 화면의 안내문이 된다.
 */
export interface ManualCover {
  /** 레포 기준 원본 경로 (`docs/manual/composer/index.md`) */
  path: string;
  manual: string;
  title: string;
  summary: string;
  order: number;
  audience: string[];
  /** 카드 아이콘 키 (선택). */
  icon?: string;
  incomplete: boolean;
  missingKeys: string[];
  body: string;
}

export interface ManualSectionNode {
  id: string;
  label: string;
  docs: ManualDoc[];
}

/** 매뉴얼 하나 — 표지 + 그 매뉴얼이 실제로 채운 섹션들. */
export interface ManualNode {
  /** 매뉴얼 id = URL 첫 단계 (`composer`) */
  id: string;
  /** 표지 title. 표지가 없으면 id로 대체된다(경고와 함께). */
  title: string;
  summary: string;
  /** 매뉴얼 목록에서의 순서. 표지가 없으면 맨 뒤. */
  order: number;
  /** 표지 문서 — 없을 수 있다(경고 대상). */
  cover?: ManualCover;
  /** 문서가 있는 섹션만 (규약 순서). */
  sections: ManualSectionNode[];
  /** 이 매뉴얼의 정렬이 끝난 전체 문서. */
  docs: ManualDoc[];
}

export interface ManualIndex {
  /** 표지 order 오름차순 (동률이면 매뉴얼 id 사전순). */
  manuals: ManualNode[];
  /** 매뉴얼·섹션·order 정렬이 끝난 전체 문서 (P4 검색이 소비). */
  docs: ManualDoc[];
}

/** 프론트매터 원시 값 — 스칼라 또는 문자열 배열. */
export type FrontmatterValue = string | string[];

export interface ParsedFrontmatter {
  fields: Record<string, FrontmatterValue>;
  body: string;
}

const FRONTMATTER_RE = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;

/** 선행 BOM 제거 (에디터에 따라 붙는다 — 프론트매터 인식을 막는다). */
function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/** 앞뒤 따옴표 한 겹을 벗긴다. */
function stripQuotes(value: string): string {
  const t = value.trim();
  if (t.length < 2) return t;
  const first = t[0];
  const last = t[t.length - 1];
  if ((first === "'" && last === "'") || (first === '"' && last === '"')) {
    return t.slice(1, -1);
  }
  return t;
}

/**
 * `---`로 감싼 프론트매터 블록을 파싱한다.
 *
 * 지원 문법은 규약이 쓰는 범위로 한정 — 스칼라(`key: value`),
 * 인라인 배열(`key: [a, b]`), 블록 배열(`key:` + `- item`). 중첩 맵은 지원하지 않는다.
 */
export function parseFrontmatter(input: string): ParsedFrontmatter {
  const raw = stripBom(input);
  const match = FRONTMATTER_RE.exec(raw);
  if (!match) {
    return { fields: {}, body: raw };
  }

  const fields: Record<string, FrontmatterValue> = {};
  let listKey: string | null = null;

  for (const line of match[1].split(/\r?\n/)) {
    if (!line.trim() || line.trim().startsWith('#')) continue;

    const item = /^\s*-\s+(.*)$/.exec(line);
    if (item && listKey) {
      const prev = fields[listKey];
      const arr = Array.isArray(prev) ? prev : [];
      const value = stripQuotes(item[1]);
      if (value) arr.push(value);
      fields[listKey] = arr;
      continue;
    }

    const kv = /^([A-Za-z_][\w-]*)[ \t]*:[ \t]*(.*)$/.exec(line);
    if (!kv) continue;

    const key = kv[1];
    const value = kv[2].trim();

    if (value === '') {
      fields[key] = [];
      listKey = key;
      continue;
    }

    listKey = null;

    if (value.startsWith('[') && value.endsWith(']')) {
      fields[key] = value
        .slice(1, -1)
        .split(',')
        .map((part) => stripQuotes(part))
        .filter((part) => part.length > 0);
      continue;
    }

    fields[key] = stripQuotes(value);
  }

  return { fields, body: raw.slice(match[0].length) };
}

function asArray(value: FrontmatterValue | undefined): string[] {
  if (value === undefined) return [];
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

function asScalar(value: FrontmatterValue | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value[0];
  return value || undefined;
}

function asNumber(value: FrontmatterValue | undefined): number | undefined {
  const scalar = asScalar(value);
  if (scalar === undefined) return undefined;
  const n = Number(scalar);
  return Number.isFinite(n) ? n : undefined;
}

function asBoolean(value: FrontmatterValue | undefined): boolean {
  const scalar = asScalar(value);
  return scalar === 'true' || scalar === 'yes';
}

/** 경로 해석 결과 — 문서이거나 매뉴얼 표지. */
export type ManualPathInfo =
  | { kind: 'doc'; manual: string; section: string; slug: string }
  | { kind: 'cover'; manual: string };

/**
 * 파일 경로에서 매뉴얼·섹션·slug를 도출한다.
 *
 * - `docs/manual/composer/concepts/c1-two-layers.md`
 *   → `{ kind: 'doc', manual: 'composer', section: 'concepts', slug: 'c1-two-layers' }`
 * - `docs/manual/composer/index.md` → `{ kind: 'cover', manual: 'composer' }`
 * - `docs/manual/README.md`(집필 규약)·깊이가 안 맞는 경로는 `null` (렌더 대상 아님).
 */
export function parseDocPath(filePath: string): ManualPathInfo | null {
  const normalized = filePath.replace(/\\/g, '/');
  const marker = 'docs/manual/';
  const at = normalized.lastIndexOf(marker);
  if (at < 0) return null;

  const parts = normalized.slice(at + marker.length).split('/').filter(Boolean);
  const last = parts[parts.length - 1];
  if (!last?.endsWith('.md')) return null;

  if (parts.length === 2) {
    // `<매뉴얼>/index.md`만 표지 — 그 외 2단계 파일(`<매뉴얼>/foo.md`)은 규약 밖이라 제외.
    return last === 'index.md' ? { kind: 'cover', manual: parts[0] } : null;
  }
  if (parts.length !== 3) return null;

  return { kind: 'doc', manual: parts[0], section: parts[1], slug: last.slice(0, -3) };
}

/** 프론트매터 필수 키 중 비어 있는 것. */
function missingRequiredKeys(fields: Record<string, FrontmatterValue>): string[] {
  return REQUIRED_KEYS.filter((key) => {
    const value = fields[key];
    if (value === undefined) return true;
    return Array.isArray(value) ? value.length === 0 : value.trim() === '';
  });
}

/** 한 파일을 문서로 해석. 문서 경로가 아니면(표지·규약 밖 경로 포함) `null`. */
export function parseManualFile(filePath: string, raw: string): ManualDoc | null {
  const located = parseDocPath(filePath);
  if (located?.kind !== 'doc') return null;

  const { fields, body } = parseFrontmatter(raw);
  const missingKeys = missingRequiredKeys(fields);

  const arrays = Object.fromEntries(
    ARRAY_KEYS.map((key) => [key, asArray(fields[key])]),
  ) as Record<(typeof ARRAY_KEYS)[number], string[]>;

  return {
    path: filePath.replace(/\\/g, '/'),
    manual: located.manual,
    section: located.section,
    slug: located.slug,
    title: asScalar(fields.title) ?? located.slug,
    summary: asScalar(fields.summary) ?? '',
    // order가 없으면 맨 뒤로 — 결함 표시는 별도로 남는다.
    order: asNumber(fields.order) ?? Number.MAX_SAFE_INTEGER,
    audience: arrays.audience,
    level: asScalar(fields.level),
    readingTime: asNumber(fields.readingTime),
    covers: arrays.covers,
    related: arrays.related,
    reviewedAt: asScalar(fields.reviewedAt),
    generated: asBoolean(fields.generated),
    incomplete: missingKeys.length > 0,
    missingKeys: [...missingKeys],
    body,
  };
}

/** 한 파일을 매뉴얼 표지로 해석. 표지 경로가 아니면 `null`. */
export function parseManualCover(filePath: string, raw: string): ManualCover | null {
  const located = parseDocPath(filePath);
  if (located?.kind !== 'cover') return null;

  const { fields, body } = parseFrontmatter(raw);
  const missingKeys = missingRequiredKeys(fields);

  return {
    path: filePath.replace(/\\/g, '/'),
    manual: located.manual,
    title: asScalar(fields.title) ?? located.manual,
    summary: asScalar(fields.summary) ?? '',
    // order가 없으면 목록 맨 뒤로 — 결함 표시는 별도로 남는다.
    order: asNumber(fields.order) ?? Number.MAX_SAFE_INTEGER,
    audience: asArray(fields.audience),
    icon: asScalar(fields.icon),
    incomplete: missingKeys.length > 0,
    missingKeys,
    body,
  };
}

/**
 * `audience` 필터 — 누락 시 admin으로 간주(업체용 분기는 명시할 때만).
 *
 * ⚠️ `both`는 "모든 대상"을 뜻하는 특수값이라 멤버십 비교에서 제외하면 안 된다.
 * 규약(README)·결정 기록이 `both` 표기를 허용하므로, 이걸 빼면 `audience: [both]` 문서가
 * **아무 경고 없이 목록에서 사라진다**(조용한 누락).
 *
 * 문서·표지 양쪽에 같은 규칙을 적용한다(둘 다 `audience`를 갖는다).
 */
export function matchesAudience(doc: { audience: string[] }, audience: string): boolean {
  if (doc.audience.length === 0) return audience === 'admin';
  if (doc.audience.includes('both')) return true;
  return doc.audience.includes(audience);
}

/** 같은 섹션 안 정렬: order 오름차순, 동률이면 slug 사전순. */
export function compareDocs(a: ManualDocMeta, b: ManualDocMeta): number {
  if (a.order !== b.order) return a.order - b.order;
  return a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0;
}

/** 매뉴얼 목록 정렬: 표지 order 오름차순, 동률이면 매뉴얼 id 사전순. */
export function compareManuals(
  a: { order: number; id: string },
  b: { order: number; id: string },
): number {
  if (a.order !== b.order) return a.order - b.order;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export interface BuildManualIndexOptions {
  audience?: string;
  /** 경고 출력 (테스트에서 주입). */
  onWarn?: (message: string) => void;
}

/**
 * 파일 맵(`경로 → 원문`)을 **매뉴얼 → 섹션 → 문서** 3층 트리로 만든다.
 *
 * - 규약에 없는 섹션 디렉토리, `docs/manual/README.md`는 제외
 * - `audience`가 맞지 않는 문서·표지 제외
 * - 필수 키가 빠진 문서·표지는 **제외하지 않고** 경고 + `incomplete` 표시로 남긴다
 * - 표지(`index.md`)가 없는 매뉴얼은 경고 + 제목을 매뉴얼 id로 대체 (문서는 버리지 않는다)
 */
export function buildManualIndex(
  files: Record<string, string>,
  options: BuildManualIndexOptions = {},
): ManualIndex {
  const audience = options.audience ?? 'admin';
  const warn = options.onWarn ?? ((message: string) => console.warn(message));

  /** 매뉴얼 id → 섹션 id → 문서 */
  const docsByManual = new Map<string, Map<string, ManualDoc[]>>();
  const coverByManual = new Map<string, ManualCover>();

  for (const [filePath, raw] of Object.entries(files)) {
    const located = parseDocPath(filePath);
    if (!located) {
      // 렌더 대상이 아닌 이유가 둘이다 — README.md(정상 제외)와 **깊이가 안 맞는 경로**(사고).
      // URL 규약상 `<매뉴얼>/<섹션>/<파일>.md` 세 단계로 고정이라, 더 깊이 넣거나 얕게 둔 문서는
      // 제외된다. 경고가 없으면 "썼는데 목록에 없다"를 원인 불명으로 헤매게 된다.
      const normalized = filePath.replace(/\\/g, '/');
      if (normalized.includes('docs/manual/') && !normalized.endsWith('/README.md')) {
        warn(
          `[manual] 경로 깊이가 규약과 다릅니다(<매뉴얼>/<섹션>/<파일>.md 로 고정) — 제외: ${normalized}`,
        );
      }
      continue;
    }

    if (located.kind === 'cover') {
      const cover = parseManualCover(filePath, raw);
      if (!cover) continue;
      if (!matchesAudience(cover, audience)) continue;
      if (cover.incomplete) {
        warn(`[manual] 표지 프론트매터 필수 키 누락(${cover.missingKeys.join(', ')}): ${cover.path}`);
      }
      coverByManual.set(cover.manual, cover);
      continue;
    }

    const doc = parseManualFile(filePath, raw);
    if (!doc) continue;

    if (!SECTION_ORDER.has(doc.section)) {
      warn(`[manual] 알 수 없는 섹션 "${doc.section}" — 렌더 대상에서 제외: ${doc.path}`);
      continue;
    }
    if (!matchesAudience(doc, audience)) continue;

    if (doc.incomplete) {
      warn(
        `[manual] 프론트매터 필수 키 누락(${doc.missingKeys.join(', ')}): ${doc.path}`,
      );
    }

    let bySection = docsByManual.get(doc.manual);
    if (!bySection) {
      bySection = new Map<string, ManualDoc[]>();
      docsByManual.set(doc.manual, bySection);
    }
    const list = bySection.get(doc.section);
    if (list) list.push(doc);
    else bySection.set(doc.section, [doc]);
  }

  // 문서만 있는 매뉴얼 + 표지만 있는 매뉴얼을 모두 후보로 (표지 없이도 문서는 살린다).
  const manualIds = new Set<string>([...docsByManual.keys(), ...coverByManual.keys()]);

  const manuals: ManualNode[] = [...manualIds]
    .map((id) => {
      const cover = coverByManual.get(id);
      if (!cover) {
        // 표지가 없으면 목록에 제목·설명이 안 나온다 — 조용히 두면 "왜 id가 보이지"로 헤맨다.
        warn(`[manual] 매뉴얼 표지(index.md)가 없습니다 — 제목을 id로 대체: docs/manual/${id}/index.md`);
      }
      const bySection = docsByManual.get(id) ?? new Map<string, ManualDoc[]>();
      // 6개 섹션 어휘는 공용이지만 매뉴얼이 전부 채울 필요는 없다 — 빈 섹션은 표시하지 않는다.
      const sections: ManualSectionNode[] = MANUAL_SECTIONS.filter((section) =>
        bySection.has(section.id),
      ).map((section) => ({
        id: section.id,
        label: section.label,
        docs: [...(bySection.get(section.id) ?? [])].sort(compareDocs),
      }));

      return {
        id,
        title: cover?.title || id,
        summary: cover?.summary ?? '',
        order: cover?.order ?? Number.MAX_SAFE_INTEGER,
        cover,
        sections,
        docs: sections.flatMap((section) => section.docs),
      };
    })
    .sort(compareManuals);

  return { manuals, docs: manuals.flatMap((manual) => manual.docs) };
}
